import BaseController from "./BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import Fragment from "sap/ui/core/Fragment";
import type ComboBox from "sap/m/ComboBox";
import type { ComboBox$SelectionChangeEvent } from "sap/m/ComboBox";
import type { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import type Context from "sap/ui/model/odata/v4/Context";
import type { ListItemBase$PressEvent } from "sap/m/ListItemBase";
import type { Button$PressEvent } from "sap/m/Button";
import type { Switch$ChangeEvent } from "sap/m/Switch";
import type Dialog from "sap/m/Dialog";
import type TextArea from "sap/m/TextArea";
import type { ResponseError } from "../types/Errors";
import { problemMessage } from "../util/ProblemDetails";

interface EntitlementSummary {
	Id: string;
	OwnerOrgId: string;
	OwnerName?: string;
	PurchasedQuantity: number;
	Status: string;
	PaymentState: string;
	ValidUntil: string;
}

interface AttributeRow {
	key: string;
	type: string;
	value: string;
	compatibility: boolean;
	allowedValuesText: string;
}

type ValueState = "None" | "Error";

interface AttrEditState {
	dialogTitle: string;
	key: string;
	keyEditable: boolean;
	keyValueState: ValueState;
	type: string;
	value: string;
	valueValueState: ValueState;
	compatibility: boolean;
	allowedValuesText: string;
	allowedValuesValueState: ValueState;
}

const EMPTY_ATTR_EDIT: AttrEditState = {
	dialogTitle: "",
	key: "",
	keyEditable: true,
	keyValueState: "None",
	type: "string",
	value: "",
	valueValueState: "None",
	compatibility: false,
	allowedValuesText: "",
	allowedValuesValueState: "None"
};

interface AttributeTemplate {
	key: string;
	type: string;
	value: string;
	compatibility: boolean;
	allowedValues: string[] | null;
	productCount: number;
	sampleProductCode: string;
}

interface TemplateItem {
	id: string;              // indeks jako string — klucz core:ListItem
	key: string;
	info: string;            // "{type} · {value} ({count}×, {sampleCode})"
	template: AttributeTemplate;
}

/**
 * Draft edytowalnych pól produktu — odpowiada PATCH /api/v1/products/{id}.
 * <c>billingPeriodDays</c> jako string ("" lub liczba) — Input typu number tak wygodniej
 * obsługuje pusty stan (= null po stronie API).
 */
interface ProductEditDraft {
	name: string;
	description: string;
	isLocationAware: boolean;
	billingPeriodDays: string;
	permissionKey: string;
}

interface ViewState {
	productId: string;
	familyId: string;
	familyName: string;
	entitlements: EntitlementSummary[];
	attributes: AttributeRow[];
	kpi: {
		activeEntitlements: string;
	};
	isEditMode: boolean;
	editDraft: ProductEditDraft;
}

const EMPTY_DRAFT: ProductEditDraft = {
	name: "",
	description: "",
	isLocationAware: false,
	billingPeriodDays: "",
	permissionKey: ""
};

/**
 * @namespace computec.b2b.portal.controller
 */
export default class ProductDetail extends BaseController {
	private productId = "";
	private attributeDialog: Dialog | null = null;
	private deactivateDialog: Dialog | null = null;

	public override onInit(): void {
		const initial: ViewState = {
			productId: "",
			familyId: "",
			familyName: "",
			entitlements: [],
			attributes: [],
			kpi: { activeEntitlements: "—" },
			isEditMode: false,
			editDraft: { ...EMPTY_DRAFT }
		};
		this.getView().setModel(new JSONModel(initial), "view");
		this.getView().setModel(new JSONModel({ ...EMPTY_ATTR_EDIT }), "attrEdit");
		this.getView().setModel(new JSONModel({ items: [] as TemplateItem[], busy: false }), "attrTemplates");
		this.getRouter().getRoute("productDetail")?.attachPatternMatched((e) => {
			this.onPatternMatched(e);
		});
	}

	private onPatternMatched(event: Route$PatternMatchedEvent): void {
		const args = event.getParameter("arguments") as { id?: string } | undefined;
		const id = args?.id ?? "";
		this.productId = id;
		const view = this.getView().getModel("view") as JSONModel;
		view.setProperty("/productId", id);
		// Wejście na inny produkt wychodzi z edit mode bez zapisu — niezapisany draft
		// jest porzucany świadomie (route change ≡ Cancel).
		view.setProperty("/isEditMode", false);
		view.setProperty("/editDraft", { ...EMPTY_DRAFT });

		this.getView().bindElement({
			path: `/Products(${id})`,
			model: "odata"
		});

		void this.loadFamilyName(id);
		void this.loadEntitlements(id);
		void this.loadAttributes(id);
	}

	private async loadFamilyName(productId: string): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const product = await fetcher.json<{ ProductFamilyId?: string }>(
				`/odata/v4/Products(${productId})?$select=ProductFamilyId`
			);
			if (!product.ProductFamilyId) {
				return;
			}
			view.setProperty("/familyId", product.ProductFamilyId);
			const family = await fetcher.json<{ Name?: string }>(
				`/odata/v4/ProductFamilies(${product.ProductFamilyId})?$select=Name`
			);
			if (family.Name) {
				view.setProperty("/familyName", family.Name);
			}
		} catch {
			view.setProperty("/familyName", "—");
		}
	}

	private async loadEntitlements(productId: string): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const permissions = this.getOwnerComponent().getPermissions();
		// The Entitlements feed is behind entitlements.read and the owner names behind orgs.read; the
		// section that shows them is gated in the view, this keeps the calls from going out as 403s.
		if (!permissions.has("entitlements.read")) {
			view.setProperty("/entitlements", []);
			return;
		}
		const canReadOrganizations = permissions.has("orgs.read");
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const data = await fetcher.json<{ value: EntitlementSummary[] }>(
				`/odata/v4/Entitlements?$filter=ProductId%20eq%20${productId}&$orderby=CreatedAt%20desc&$select=Id,OwnerOrgId,PurchasedQuantity,Status,PaymentState,ValidUntil`
			);
			const entitlements = Array.isArray(data.value) ? data.value : [];

			// Resolve owner names in parallel — reasonable bo zwykle <20 entitlementów per produkt.
			await Promise.all(entitlements.map(async (e) => {
				if (!canReadOrganizations) {
					e.OwnerName = e.OwnerOrgId;
					return;
				}
				try {
					const org = await fetcher.json<{ Name?: string }>(
						`/odata/v4/Organizations(${e.OwnerOrgId})?$select=Name`
					);
					e.OwnerName = org.Name ?? e.OwnerOrgId;
				} catch {
					e.OwnerName = e.OwnerOrgId;
				}
			}));

			view.setProperty("/entitlements", entitlements);
			const active = entitlements.filter(e => e.Status === "Active").length;
			view.setProperty("/kpi/activeEntitlements", String(active));
		} catch {
			view.setProperty("/entitlements", []);
		}
	}

	private async loadAttributes(productId: string): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const data = await fetcher.json<Record<string, { type: string; value: string; compatibility: boolean; allowedValues: string[] | null }>>(
				`/api/v1/products/${productId}/attributes`
			);
			const rows: AttributeRow[] = Object.entries(data).map(([key, p]) => ({
				key,
				type: p.type,
				value: p.value,
				compatibility: p.compatibility,
				allowedValuesText: (p.allowedValues ?? []).join(", ")
			}));
			view.setProperty("/attributes", rows);
		} catch {
			view.setProperty("/attributes", []);
		}
	}

	public onNavBack(): void {
		this.navTo("products", {});
	}

	public onOpenFamily(): void {
		const familyId = (this.getView().getModel("view") as JSONModel).getProperty("/familyId") as string;
		if (familyId) {
			this.navTo("productFamilyDetail", { id: familyId });
		}
	}

	public onRefresh(): void {
		const id = (this.getView().getModel("view") as JSONModel).getProperty("/productId") as string;
		if (id) {
			this.getView().getModel("odata")?.refresh();
			void this.loadEntitlements(id);
			void this.loadAttributes(id);
		}
	}

	/**
	 * Toggle Product.IsHidden. Endpoint <c>PATCH /api/v1/products/{id}/visibility</c>
	 * with body <c>{ hidden: bool }</c> (permission <c>catalog.manage</c>). The switch is
	 * bound to <c>odata>IsHidden</c> — on error we revert the state manually, on success
	 * we force a refresh (the OData v4 cache does not refresh itself after a REST call).
	 */
	public async onVisibilityChanged(event: Switch$ChangeEvent): Promise<void> {
		const sw = event.getSource();
		const hidden = sw.getState();
		const id = (this.getView().getModel("view") as JSONModel).getProperty("/productId") as string;
		if (!id) {
			return;
		}

		const bundle = await this.getResourceBundle();
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const r = await fetcher.fetch(`/api/v1/products/${id}/visibility`, {
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ hidden })
			});
			if (!r.ok) {
				const bodyText = await r.text();
				const err = new Error(`HTTP ${r.status}`) as Error & ResponseError;
				err.status = r.status;
				err.bodyText = bodyText;
				throw err;
			}
			MessageToast.show(bundle.getText("product.visibility.savedToast") ?? "Visibility updated");
			this.getView().getModel("odata")?.refresh();
		} catch (e) {
			sw.setState(!hidden);
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("product.visibility.errorToast") ?? "Failed to update visibility"));
		}
	}

	// === Edit mode (PATCH /api/v1/products/{id}) ===

	/**
	 * Wchodzi w tryb edycji — kopiuje aktualne wartości z bind contextu OData do <c>editDraft</c>.
	 * Pola immutable po Create (Code, BillingMode, LicensingMode, BundleKind, PackSize) nie
	 * trafiają do drafta — są render-only także w trybie edit.
	 */
	public onEnterEditMode(): void {
		const ctx = this.getView().getBindingContext("odata");
		if (!ctx) {
			return;
		}
		const view = this.getView().getModel("view") as JSONModel;
		const billingPeriodDays = ctx.getProperty("BillingPeriodDays") as number | null | undefined;
		const draft: ProductEditDraft = {
			name: (ctx.getProperty("Name") as string | undefined) ?? "",
			description: (ctx.getProperty("Description") as string | undefined) ?? "",
			isLocationAware: Boolean(ctx.getProperty("IsLocationAware")),
			billingPeriodDays: billingPeriodDays != null ? String(billingPeriodDays) : "",
			permissionKey: (ctx.getProperty("PermissionKey") as string | undefined) ?? ""
		};
		view.setProperty("/editDraft", draft);
		view.setProperty("/isEditMode", true);
	}

	public onCancelEdit(): void {
		const view = this.getView().getModel("view") as JSONModel;
		view.setProperty("/isEditMode", false);
		view.setProperty("/editDraft", { ...EMPTY_DRAFT });
	}

	public async onSaveProfile(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const view = this.getView().getModel("view") as JSONModel;
		const id = view.getProperty("/productId") as string;
		const draft = view.getProperty("/editDraft") as ProductEditDraft;

		const name = draft.name.trim();
		if (name.length === 0) {
			MessageBox.warning(bundle.getText("productDetail.edit.error.nameRequired") ?? "Name is required.");
			return;
		}

		// BillingPeriodDays: "" → null; liczba → number. Domain odrzuci nielegalne kombinacje
		// (np. non-null na Perpetual) — 422 zostanie pokazane w MessageBox.
		let billingPeriodDays: number | null = null;
		if (draft.billingPeriodDays.trim().length > 0) {
			const n = Number(draft.billingPeriodDays);
			if (!Number.isFinite(n) || n < 1) {
				MessageBox.warning(bundle.getText("productDetail.edit.error.billingPeriodInvalid") ?? "Billing period must be ≥ 1.");
				return;
			}
			billingPeriodDays = n;
		}

		const payload = {
			name,
			description: draft.description.trim().length > 0 ? draft.description.trim() : null,
			isLocationAware: draft.isLocationAware,
			billingPeriodDays,
			permissionKey: draft.permissionKey.trim().length > 0 ? draft.permissionKey.trim() : null
		};

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const r = await fetcher.fetch(`/api/v1/products/${id}`, {
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload)
			});
			if (!r.ok) {
				const bodyText = await r.text();
				// Rzucamy Error (a nie plain object) bo @typescript-eslint/only-throw-error;
				// catch poniżej rzutuje na ResponseError przez właściwości na Error instance.
				const err = new Error(`HTTP ${r.status}`) as Error & ResponseError;
				err.status = r.status;
				err.bodyText = bodyText;
				throw err;
			}
			MessageToast.show(bundle.getText("productDetail.edit.success") ?? "Saved.");
			view.setProperty("/isEditMode", false);
			view.setProperty("/editDraft", { ...EMPTY_DRAFT });
			// Refresh bind contextu — pokazane wartości zsynchronizują się z bazą.
			this.getView().getModel("odata")?.refresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("productDetail.edit.failure") ?? "Could not save product."));
		}
	}

	// === Product attribute dialog (add / edit) ===

	public async onAddAttribute(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const model = this.getView().getModel("attrEdit") as JSONModel;
		model.setData({
			...EMPTY_ATTR_EDIT,
			dialogTitle: bundle.getText("productDetail.params.dialog.addTitle") ?? "Add product parameter",
			keyEditable: true
		});
		void this.loadAttributeTemplates();
		await this.openAttributeDialog();
	}

	/**
	 * Ładuje agregat istniejących definicji parametrów (ze wszystkich produktów) do ComboBoxa
	 * prefillu. Fire-and-forget — błąd nie blokuje dialogu, szablon jest opcjonalny.
	 */
	private async loadAttributeTemplates(): Promise<void> {
		// Reset wyboru PRZED fetchem — przy ponownym otwarciu dialogu stary wybór nie może
		// wisieć w ComboBoxie przez czas round-tripu (przy pierwszym otwarciu fragment jeszcze
		// nie istnieje — optional chaining robi no-op).
		const cb = this.byId("cbAttrTemplate") as ComboBox | undefined;
		cb?.setSelectedKey("");
		cb?.setValue("");

		const m = this.getView().getModel("attrTemplates") as JSONModel;
		m.setProperty("/busy", true);
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const templates = await fetcher.json<AttributeTemplate[]>("/api/v1/products/attribute-templates");
			m.setProperty("/items", templates.map((t, i): TemplateItem => ({
				id: String(i),
				key: t.key,
				info: `${t.type} · ${t.value} (${t.productCount}×, ${t.sampleProductCode})`,
				template: t
			})));
		} catch (e) {
			// Prefill jest opcjonalny — brak szablonów nie blokuje ręcznego dodania parametru.
			console.warn("attribute templates load failed", e);
			m.setProperty("/items", []);
		} finally {
			m.setProperty("/busy", false);
		}
	}

	public onTemplateSelected(event: ComboBox$SelectionChangeEvent): void {
		const item = event.getParameter("selectedItem");
		if (!item) {
			return;
		}
		const templates = (this.getView().getModel("attrTemplates") as JSONModel).getProperty("/items") as TemplateItem[];
		const chosen = templates.find(t => t.id === item.getKey());
		if (!chosen) {
			return;
		}
		const edit = this.getView().getModel("attrEdit") as JSONModel;
		edit.setProperty("/key", chosen.template.key);
		edit.setProperty("/type", chosen.template.type);
		edit.setProperty("/value", chosen.template.value);
		edit.setProperty("/compatibility", chosen.template.compatibility);
		edit.setProperty("/allowedValuesText", (chosen.template.allowedValues ?? []).join(", "));
		edit.setProperty("/keyValueState", "None");
		edit.setProperty("/valueValueState", "None");
		edit.setProperty("/allowedValuesValueState", "None");
	}

	public async onEditAttribute(event: Button$PressEvent): Promise<void> {
		const bundle = await this.getResourceBundle();
		const ctx = event.getSource().getBindingContext("view") as Context | null;
		if (!ctx) {
			return;
		}
		const row = ctx.getObject() as AttributeRow;
		const model = this.getView().getModel("attrEdit") as JSONModel;
		model.setData({
			dialogTitle: bundle.getText("productDetail.params.dialog.editTitle") ?? "Edit product parameter",
			key: row.key,
			keyEditable: false,
			keyValueState: "None",
			type: row.type,
			value: row.value,
			valueValueState: "None",
			compatibility: row.compatibility,
			allowedValuesText: row.allowedValuesText,
			allowedValuesValueState: "None"
		} satisfies AttrEditState);
		await this.openAttributeDialog();
	}

	private async openAttributeDialog(): Promise<void> {
		if (!this.attributeDialog) {
			this.attributeDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.ProductAttributeDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.attributeDialog);
		}
		this.attributeDialog.open();
	}

	public onCancelAttribute(): void {
		this.attributeDialog?.close();
	}

	public async onSaveAttribute(): Promise<void> {
		const m = (this.getView().getModel("attrEdit") as JSONModel).getData() as AttrEditState;
		const bundle = await this.getResourceBundle();

		// Soft validation — wyczyść poprzednie stany błędu, by pole, które stało się poprawne,
		// przestało świecić na czerwono w obrębie tej samej sesji dialogu.
		const editModel = this.getView().getModel("attrEdit") as JSONModel;
		editModel.setProperty("/keyValueState", "None");
		editModel.setProperty("/valueValueState", "None");
		editModel.setProperty("/allowedValuesValueState", "None");
		let hasError = false;
		if (!m.key.trim()) {
			(this.getView().getModel("attrEdit") as JSONModel).setProperty("/keyValueState", "Error");
			hasError = true;
		}
		if (!m.value.trim()) {
			(this.getView().getModel("attrEdit") as JSONModel).setProperty("/valueValueState", "Error");
			hasError = true;
		}
		if (m.type === "enum" && !m.allowedValuesText.trim()) {
			(this.getView().getModel("attrEdit") as JSONModel).setProperty("/allowedValuesValueState", "Error");
			hasError = true;
		}
		if (hasError) {
			return;
		}

		const allowedValues = m.type === "enum"
			? m.allowedValuesText.split(",").map(s => s.trim()).filter(s => s.length > 0)
			: null;

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/products/${this.productId}/attributes`, {
				method: "POST",
				body: JSON.stringify({
					key: m.key.trim(),
					type: m.type,
					value: m.value.trim(),
					compatibility: m.compatibility,
					allowedValues
				})
			});
			this.attributeDialog?.close();
			await this.loadAttributes(this.productId);
			await this.maybePromptPropagate();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("productDetail.params.dialog.saveFailed") ?? "Could not save product parameter"));
		}
	}

	public onOpenEntitlement(event: ListItemBase$PressEvent): void {
		const ctx = event.getSource().getBindingContext("view") as Context | null;
		if (!ctx) {
			return;
		}
		const entId = ctx.getProperty("Id") as string | undefined;
		if (entId) {
			this.navTo("entitlementDetail", { id: entId });
		}
	}

	public async onDeleteAttribute(event: Button$PressEvent): Promise<void> {
		const bundle = await this.getResourceBundle();
		const ctx = event.getSource().getBindingContext("view") as Context | null;
		if (!ctx) {
			return;
		}
		const row = ctx.getObject() as AttributeRow;
		await new Promise<void>(resolve => {
			MessageBox.confirm(
				bundle.getText("productDetail.params.delete.confirm") ?? "Are you sure you want to delete this parameter?",
				{
					onClose: (action: string) => {
						// MessageBox.Action.OK = "OK" — porównujemy do stringa, żeby uniknąć
						// @typescript-eslint/no-unsafe-enum-comparison.
						if (action === "OK") {
							void (async () => {
								try {
									const fetcher = this.getOwnerComponent().getAuthFetch();
									const r = await fetcher.fetch(
										`/api/v1/products/${this.productId}/attributes/${encodeURIComponent(row.key)}`,
										{ method: "DELETE" }
									);
									if (!r.ok) {
										const bodyText = await r.text();
										const err = new Error(`HTTP ${r.status}`) as Error & ResponseError;
										err.status = r.status;
										err.bodyText = bodyText;
										throw err;
									}
									await this.loadAttributes(this.productId);
									MessageToast.show(bundle.getText("productDetail.params.delete.success") ?? "Parameter deleted.");
								} catch (e) {
									const err = e as ResponseError;
									MessageBox.error(problemMessage(err.bodyText, bundle.getText("productDetail.params.dialog.saveFailed") ?? "Could not delete parameter."));
								} finally {
									resolve();
								}
							})();
						} else {
							resolve();
						}
					}
				}
			);
		});
	}

	private maybePromptPropagate(): Promise<void> {
		return new Promise(resolve => {
			void (async () => {
				const bundle = await this.getResourceBundle();
				MessageBox.confirm(
					bundle.getText("productDetail.params.propagate.confirm") ?? "Copy changes to active licenses?",
					{
						onClose: (action: string) => {
							// MessageBox.Action.OK = "OK" — porównujemy do stringa, żeby uniknąć
							// @typescript-eslint/no-unsafe-enum-comparison.
							if (action === "OK") {
								void (async () => {
									try {
										const fetcher = this.getOwnerComponent().getAuthFetch();
										await fetcher.json(
											`/api/v1/products/${this.productId}/parameters/propagate`,
											{ method: "POST" }
										);
										const b = await this.getResourceBundle();
										MessageToast.show(b.getText("productDetail.params.propagate.success") ?? "Propagated.");
									} catch (e) {
										const err = e as ResponseError;
										MessageBox.error(problemMessage(err.bodyText, bundle.getText("productDetail.params.propagate.failed") ?? "Could not propagate changes to active licenses."));
									} finally {
										resolve();
									}
								})();
							} else {
								resolve();
							}
						}
					}
				);
			})();
		});
	}

	// === Lifecycle (POST /api/v1/products/{id}/activate | /deactivate) ===

	/**
	 * Product.Create leaves the product Inactive on purpose, so this action is the only path
	 * that makes a product created from the portal orderable. Both endpoints are REST, not
	 * OData, so the v4 cache has to be refreshed explicitly afterwards.
	 */
	public async onActivateProduct(): Promise<void> {
		const id = (this.getView().getModel("view") as JSONModel).getProperty("/productId") as string;
		if (!id) {
			return;
		}
		const bundle = await this.getResourceBundle();
		const name = (this.getView().getBindingContext("odata")?.getProperty("Name") as string) ?? "";
		MessageBox.confirm(bundle.getText("productDetail.activate.confirm", [name]) ?? "Activate this product?", {
			title: bundle.getText("productDetail.action.activate") ?? "Activate",
			onClose: (action: unknown) => {
				if (action === MessageBox.Action.OK) {
					void this.sendLifecycle(
						`/api/v1/products/${id}/activate`,
						"{}",
						bundle.getText("productDetail.activate.success") ?? "Product activated.",
						bundle.getText("productDetail.activate.failure") ?? "Could not activate the product."
					);
				}
			}
		});
	}

	public async onDeactivateProduct(): Promise<void> {
		const bundle = await this.getResourceBundle();
		(this.getView().getModel("view") as JSONModel).setProperty(
			"/deactivateTitle",
			bundle.getText("productDetail.action.deactivate") ?? "Deactivate"
		);
		await this.openDeactivateDialog();
	}

	public async onConfirmDeactivate(): Promise<void> {
		const id = (this.getView().getModel("view") as JSONModel).getProperty("/productId") as string;
		if (!id) {
			return;
		}
		const bundle = await this.getResourceBundle();
		const ta = this.byId("taDeactivateReason") as TextArea | undefined;
		const reason = (ta?.getValue() ?? "").trim();
		// Backend validator: Reason NotEmpty, MaximumLength(500).
		if (reason.length === 0) {
			ta?.setValueState("Error");
			ta?.setValueStateText(bundle.getText("catalog.deactivate.reasonRequired") ?? "Reason is required.");
			return;
		}
		ta?.setValueState("None");
		this.deactivateDialog?.close();
		await this.sendLifecycle(
			`/api/v1/products/${id}/deactivate`,
			JSON.stringify({ reason }),
			bundle.getText("productDetail.deactivate.success") ?? "Product deactivated.",
			bundle.getText("productDetail.deactivate.failure") ?? "Could not deactivate the product."
		);
	}

	public onCancelDeactivate(): void {
		this.deactivateDialog?.close();
	}

	private async openDeactivateDialog(): Promise<void> {
		this.deactivateDialog ??= await Fragment.load({
			id: this.getView().getId(),
			name: "computec.b2b.portal.view.DeactivateCatalogDialog",
			controller: this
		}) as Dialog;
		this.getView().addDependent(this.deactivateDialog);
		const ta = this.byId("taDeactivateReason") as TextArea | undefined;
		ta?.setValue("");
		ta?.setValueState("None");
		this.deactivateDialog.open();
	}

	private async sendLifecycle(url: string, body: string, successText: string, failureText: string): Promise<void> {
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(url, { method: "POST", body });
			MessageToast.show(successText);
			this.onRefresh();
		} catch (e) {
			MessageBox.error(problemMessage((e as ResponseError).bodyText, failureText));
		}
	}
}

